import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';

function formatBytes(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return dateStr;
  }
}

// Single Tree Node item (recursive)
function TreeNode({
  node,
  level = 0,
  selectedNode,
  expandedNodes,
  toggleExpand,
  selectNode,
  onOpenViewer,
  searchQuery
}) {
  const isExpanded = !!expandedNodes[node.id];
  const isSelected = selectedNode?.id === node.id;
  const isFolder = node.isFolder || !!node.folder;
  const hasChildren = node.children && node.children.length > 0;
  const ext = (node.name || '').split('.').pop().toLowerCase();
  const isExcel = ['xlsx', 'xls', 'xlsm', 'csv'].includes(ext);
  const isPdf = ext === 'pdf';

  // Highlight search
  const matchesSearch = searchQuery && node.name.toLowerCase().includes(searchQuery.toLowerCase());

  return (
    <div style={{ userSelect: 'none' }}>
      <div
        onClick={() => {
          if (isFolder) {
            toggleExpand(node);
          }
          selectNode(node);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 8px 6px ' + (level * 18 + 8) + 'px',
          cursor: 'pointer',
          borderRadius: '6px',
          background: isSelected
            ? '#EEF2FF'
            : matchesSearch
            ? '#FEF3C7'
            : 'transparent',
          borderLeft: isSelected ? '3px solid #4F46E5' : '3px solid transparent',
          color: isSelected ? '#1E1B4B' : 'var(--color-text, #334155)',
          fontWeight: isSelected || isFolder ? 600 : 400,
          fontSize: '13px',
          transition: 'all 0.12s ease',
          position: 'relative'
        }}
        onMouseEnter={e => {
          if (!isSelected) e.currentTarget.style.background = '#F8FAFC';
        }}
        onMouseLeave={e => {
          if (!isSelected) e.currentTarget.style.background = matchesSearch ? '#FEF3C7' : 'transparent';
        }}
      >
        {/* Expand / Collapse Caret */}
        {isFolder ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(node);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              fontSize: '10px',
              color: '#64748B',
              cursor: 'pointer',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease'
            }}
          >
            ▶
          </span>
        ) : (
          <span style={{ width: '16px', display: 'inline-block' }} />
        )}

        {/* Node Icon */}
        <span style={{ fontSize: '15px', flexShrink: 0 }}>
          {isFolder ? (isExpanded ? '📂' : '📁') : isExcel ? '📊' : isPdf ? '📄' : '📋'}
        </span>

        {/* Node Label */}
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          color: isSelected ? '#3730A3' : '#1E293B'
        }}>
          {node.name}
        </span>

        {/* Sub-item count or file size badge */}
        {isFolder ? (
          node.childrenCount !== undefined && node.childrenCount > 0 ? (
            <span style={{
              fontSize: '10.5px',
              background: '#F1F5F9',
              color: '#64748B',
              padding: '1px 6px',
              borderRadius: '99px',
              fontWeight: 700
            }}>
              {node.childrenCount}
            </span>
          ) : null
        ) : (
          <span style={{
            fontSize: '10.5px',
            color: '#94A3B8',
            fontFamily: 'monospace'
          }}>
            {formatBytes(node.size || node.sizeBytes)}
          </span>
        )}
      </div>

      {/* Children recursive rendering */}
      {isFolder && isExpanded && (
        <div style={{ borderLeft: level > 0 ? '1px dashed #E2E8F0' : 'none', marginLeft: level > 0 ? (level * 18 + 15) + 'px' : 0 }}>
          {node.loading ? (
            <div style={{ padding: '6px 0 6px ' + (level * 18 + 26) + 'px', fontSize: '12px', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Loading folder...
            </div>
          ) : node.children && node.children.length > 0 ? (
            node.children.map(child => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                selectedNode={selectedNode}
                expandedNodes={expandedNodes}
                toggleExpand={toggleExpand}
                selectNode={selectNode}
                onOpenViewer={onOpenViewer}
                searchQuery={searchQuery}
              />
            ))
          ) : (
            <div style={{ padding: '4px 0 4px ' + (level * 18 + 26) + 'px', fontSize: '11.5px', color: '#94A3B8', fontStyle: 'italic' }}>
              (empty folder)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FolderTreeView({ user, isAdminOrManager = true }) {
  const navigate = useNavigate();
  const [treeData, setTreeData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [searchTree, setSearchTree] = useState('');
  const [searchFolderContent, setSearchFolderContent] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  // Fetch Root Directories and Build Initial Tree Hierarchy
  const loadRootTree = useCallback(async () => {
    setLoadingTree(true);
    try {
      // 1. Fetch operations summary to build categorized top branches
      const summaryRes = await adminApi.operationsSummary(false);
      const folders = summaryRes.data?.folders || [];

      // Group folders by Category
      const categoryMap = new Map();
      folders.forEach(f => {
        const cat = f.category || 'General Operations';
        if (!categoryMap.has(cat)) categoryMap.set(cat, []);
        categoryMap.get(cat).push({
          id: f.id,
          name: f.name,
          isFolder: true,
          category: f.category,
          childrenCount: f.totalFiles,
          children: null // lazy load
        });
      });

      // Construct Root Categories
      const initialTree = [];
      for (const [catName, catFolders] of categoryMap.entries()) {
        initialTree.push({
          id: 'cat_' + catName.replace(/\s+/g, '_').toLowerCase(),
          name: `📁 ${catName}`,
          isCategory: true,
          isFolder: true,
          childrenCount: catFolders.length,
          children: catFolders
        });
      }

      setTreeData(initialTree);

      // Auto-expand first category and select first folder
      if (initialTree.length > 0) {
        const firstCat = initialTree[0];
        const newExpanded = { [firstCat.id]: true };
        if (firstCat.children && firstCat.children.length > 0) {
          const firstFolder = firstCat.children[0];
          setSelectedNode(firstFolder);
          loadFolderFiles(firstFolder);
        }
        setExpandedNodes(newExpanded);
      }
    } catch (err) {
      console.error('Failed to load tree:', err);
      toast.error('Failed to load folder hierarchy');
    } finally {
      setLoadingTree(false);
    }
  }, []);

  useEffect(() => {
    loadRootTree();
  }, [loadRootTree]);

  // Lazy Load Children for any folder node
  const loadFolderFiles = async (node) => {
    if (!node || node.isCategory) {
      setFolderFiles([]);
      return;
    }

    setLoadingFiles(true);
    try {
      const res = await adminApi.listOneDrive({ folderId: node.id });
      const items = res.data.items || [];
      setFolderFiles(items);

      // Update children in treeData
      setTreeData(prevTree => {
        const updateChildren = (nodes) => {
          return nodes.map(n => {
            if (n.id === node.id) {
              return {
                ...n,
                children: items.map(item => ({
                  id: item.id,
                  name: item.name,
                  isFolder: item.isFolder || !!item.folder,
                  size: item.size || item.sizeBytes,
                  lastModified: item.lastModifiedDateTime,
                  webUrl: item.webUrl,
                  children: null
                })),
                childrenCount: items.length
              };
            }
            if (n.children) {
              return { ...n, children: updateChildren(n.children) };
            }
            return n;
          });
        };
        return updateChildren(prevTree);
      });
    } catch (err) {
      console.error('Failed to load folder files:', err);
    } finally {
      setLoadingFiles(false);
    }
  };

  const toggleExpand = async (node) => {
    const isNowExpanded = !expandedNodes[node.id];
    setExpandedNodes(prev => ({ ...prev, [node.id]: isNowExpanded }));

    if (isNowExpanded && !node.isCategory && (!node.children || node.children.length === 0)) {
      await loadFolderFiles(node);
    }
  };

  const handleSelectNode = (node) => {
    setSelectedNode(node);
    if (!node.isCategory && (node.isFolder || !!node.folder)) {
      loadFolderFiles(node);
    }
  };

  const handleExpandAll = () => {
    const all = {};
    const recurse = (nodes) => {
      nodes.forEach(n => {
        if (n.isFolder) all[n.id] = true;
        if (n.children) recurse(n.children);
      });
    };
    recurse(treeData);
    setExpandedNodes(all);
  };

  const handleCollapseAll = () => {
    setExpandedNodes({});
  };

  const filteredFolderFiles = useMemo(() => {
    if (!searchFolderContent.trim()) return folderFiles;
    const q = searchFolderContent.toLowerCase();
    return folderFiles.filter(f => f.name.toLowerCase().includes(q));
  }, [folderFiles, searchFolderContent]);

  const handleOpenFile = (file) => {
    navigate(`/onedrive/view/${file.id}?name=${encodeURIComponent(file.name)}`);
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(300px, 360px) 1fr',
      gap: '20px',
      background: 'var(--color-surface, #FFFFFF)',
      borderRadius: '20px',
      border: '1px solid var(--color-border, #E2E8F0)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
      overflow: 'hidden',
      minHeight: '720px'
    }}>
      {/* ── LEFT PANE: PC Explorer Folder Tree ───────────────────────── */}
      <div style={{
        borderRight: '1px solid var(--color-border, #E2E8F0)',
        background: '#FAFBFD',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        {/* Left Pane Header */}
        <div style={{
          padding: '18px 16px 14px',
          borderBottom: '1px solid #E2E8F0',
          background: '#FFFFFF'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '18px' }}>🌳</span>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Folder Hierarchy
              </h3>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={handleExpandAll}
                className="btn btn-sm"
                style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 700, background: '#F1F5F9', border: '1px solid #E2E8F0', color: '#475569' }}
                title="Expand all tree folders"
              >
                [+] Expand
              </button>
              <button
                onClick={handleCollapseAll}
                className="btn btn-sm"
                style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 700, background: '#F1F5F9', border: '1px solid #E2E8F0', color: '#475569' }}
                title="Collapse all tree folders"
              >
                [-] Collapse
              </button>
            </div>
          </div>

          {/* Tree Search */}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px' }}>
              🔍
            </span>
            <input
              type="text"
              className="input"
              value={searchTree}
              onChange={e => setSearchTree(e.target.value)}
              placeholder="Filter folder hierarchy..."
              style={{ paddingLeft: '30px', height: '34px', fontSize: '12.5px', borderRadius: '8px', width: '100%', background: '#F8FAFC' }}
            />
            {searchTree && (
              <button
                onClick={() => setSearchTree('')}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8', fontSize: '11px' }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Tree Body */}
        <div style={{
          padding: '12px 8px',
          overflowY: 'auto',
          flex: 1,
          maxHeight: '650px'
        }}>
          {loadingTree ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748B', fontSize: '13px' }}>
              <div style={{ fontSize: '24px', marginBottom: 8, animation: 'spin 1.2s linear infinite' }}>⏳</div>
              Loading folder structure...
            </div>
          ) : treeData.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
              No folders found.
            </div>
          ) : (
            treeData.map(rootNode => (
              <TreeNode
                key={rootNode.id}
                node={rootNode}
                level={0}
                selectedNode={selectedNode}
                expandedNodes={expandedNodes}
                toggleExpand={toggleExpand}
                selectNode={handleSelectNode}
                onOpenViewer={handleOpenFile}
                searchQuery={searchTree}
              />
            ))
          )}
        </div>

        {/* Tree Footer / Status */}
        <div style={{
          padding: '10px 16px',
          borderTop: '1px solid #E2E8F0',
          background: '#FFFFFF',
          fontSize: '11.5px',
          color: '#64748B',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>💻 <strong>Target:</strong> OneDrive Root</span>
          <span>⚡ Live Sync</span>
        </div>
      </div>

      {/* ── RIGHT PANE: Selected Folder Contents & File Explorer ─────── */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '20px 24px 24px 0'
      }}>
        {/* Right Pane Top Bar: Breadcrumb + Search + View Mode */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 14,
          paddingBottom: '16px',
          borderBottom: '1px solid #F1F5F9',
          marginBottom: '16px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px', color: '#64748B', marginBottom: 4 }}>
              <span>💻 Root</span>
              <span>/</span>
              {selectedNode?.category && <span>{selectedNode.category} /</span>}
              <span style={{ fontWeight: 700, color: '#0F172A' }}>{selectedNode?.name || 'Select a folder'}</span>
            </div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{selectedNode?.isFolder ? '📁' : '📄'}</span>
              <span>{selectedNode?.name || 'All Files'}</span>
            </h2>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* Search inside selected folder */}
            <div style={{ position: 'relative', width: '220px' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px' }}>
                🔍
              </span>
              <input
                type="text"
                className="input"
                value={searchFolderContent}
                onChange={e => setSearchFolderContent(e.target.value)}
                placeholder="Search in folder..."
                style={{ paddingLeft: '30px', height: '36px', fontSize: '12.5px', borderRadius: '8px', width: '100%' }}
              />
            </div>

            {/* List vs Grid Toggle */}
            <div style={{ display: 'flex', background: '#F1F5F9', padding: '3px', borderRadius: '8px' }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  background: viewMode === 'list' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'list' ? '#2563EB' : '#64748B',
                  boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                📋 List
              </button>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  background: viewMode === 'grid' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'grid' ? '#2563EB' : '#64748B',
                  boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                🎴 Grid
              </button>
            </div>
          </div>
        </div>

        {/* Right Pane Body: Folder Items List or Grid */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: '580px' }}>
          {loadingFiles ? (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: 12, animation: 'spin 1.2s linear infinite' }}>⏳</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A' }}>Fetching folder contents...</div>
            </div>
          ) : !selectedNode ? (
            <div style={{ padding: '80px 20px', textAlign: 'center', color: '#64748B' }}>
              <div style={{ fontSize: '40px', marginBottom: 10 }}>👈</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Select a folder on the left hierarchy tree</div>
              <div style={{ fontSize: '13px', marginTop: 4 }}>Click any folder to view its spreadsheets, reports, and subdirectories.</div>
            </div>
          ) : filteredFolderFiles.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748B' }}>
              <div style={{ fontSize: '36px', marginBottom: 10 }}>📭</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>No files found inside "{selectedNode.name}"</div>
              <div style={{ fontSize: '13px', marginTop: 4 }}>This folder is currently empty or no files match your search filter.</div>
            </div>
          ) : viewMode === 'list' ? (
            /* List / Table Mode */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#475569', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px' }}>
                  <th style={{ padding: '12px 14px' }}>Name</th>
                  <th style={{ padding: '12px 14px' }}>Format</th>
                  <th style={{ padding: '12px 14px' }}>Size</th>
                  <th style={{ padding: '12px 14px' }}>Last Modified</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFolderFiles.map((file, idx) => {
                  const isFolder = file.isFolder || !!file.folder;
                  const ext = file.name.split('.').pop().toLowerCase();
                  const isExcel = ['xlsx', 'xls', 'xlsm', 'csv'].includes(ext);

                  return (
                    <tr
                      key={file.id || idx}
                      style={{ borderBottom: '1px solid #F1F5F9', transition: 'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 14px' }}>
                        <div
                          onClick={() => isFolder ? handleSelectNode(file) : handleOpenFile(file)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                        >
                          <span style={{ fontSize: '18px' }}>
                            {isFolder ? '📁' : isExcel ? '📊' : ext === 'pdf' ? '📄' : '📋'}
                          </span>
                          <span style={{ fontWeight: 600, color: '#0F172A', wordBreak: 'break-all' }}>
                            {file.name}
                          </span>
                        </div>
                      </td>

                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                          background: isFolder ? '#F1F5F9' : isExcel ? '#E0F2FE' : '#FEE2E2',
                          color: isFolder ? '#475569' : isExcel ? '#0369A1' : '#B91C1C',
                          padding: '2px 8px', borderRadius: '4px'
                        }}>
                          {isFolder ? 'FOLDER' : ext}
                        </span>
                      </td>

                      <td style={{ padding: '12px 14px', color: '#64748B', fontFamily: 'monospace' }}>
                        {isFolder ? '—' : formatBytes(file.size || file.sizeBytes)}
                      </td>

                      <td style={{ padding: '12px 14px', color: '#64748B', fontSize: '12px' }}>
                        {formatDate(file.lastModifiedDateTime || file.lastModified)}
                      </td>

                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        {isFolder ? (
                          <button
                            className="btn btn-sm"
                            onClick={() => handleSelectNode(file)}
                            style={{ background: '#F1F5F9', color: '#334155', border: '1px solid #E2E8F0', fontWeight: 600, fontSize: '11.5px', padding: '4px 10px' }}
                          >
                            Open ➔
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm"
                            onClick={() => handleOpenFile(file)}
                            style={{ background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', fontWeight: 600, fontSize: '11.5px', padding: '4px 10px' }}
                          >
                            👁️ View
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            /* Grid Mode */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px' }}>
              {filteredFolderFiles.map((file, idx) => {
                const isFolder = file.isFolder || !!file.folder;
                const ext = file.name.split('.').pop().toLowerCase();
                const isExcel = ['xlsx', 'xls', 'xlsm', 'csv'].includes(ext);

                return (
                  <div
                    key={file.id || idx}
                    onClick={() => isFolder ? handleSelectNode(file) : handleOpenFile(file)}
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      transition: 'all 0.15s ease',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#3B82F6';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.12)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = '#E2E8F0';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.02)';
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: 8 }}>
                      {isFolder ? '📁' : isExcel ? '📊' : ext === 'pdf' ? '📄' : '📋'}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', wordBreak: 'break-all', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {file.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748B', marginTop: 4 }}>
                      {isFolder ? 'Folder' : formatBytes(file.size || file.sizeBytes)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
